import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { ArrowLeft, Plus, Save, Power, PowerOff, Lock } from "lucide-react";

const TIERS = [
    { id: "starter", name: "Starter" },
    { id: "business", name: "Business" },
    { id: "enterprise", name: "Enterprise" },
    { id: "custom", name: "Outro" },
];

const EMPTY_PLAN = {
    id: "", tier: "starter", tier_name: "Starter", max_users: 20,
    name: "", duration_days: 30, warning_days: 7, amount: 0, currency: "eur", active: true,
};

export default function AdminPlansPage() {
    const [plans, setPlans] = useState([]);
    const [loading, setLoading] = useState(false);
    const [editing, setEditing] = useState({}); // { plan_id: {...} }
    const [creating, setCreating] = useState(false);
    const [draft, setDraft] = useState(EMPTY_PLAN);

    const load = async () => {
        setLoading(true);
        try {
            const r = await api.get("/admin/plans");
            setPlans(r.data || []);
            setEditing({});
        } catch (err) {
            toast.error(formatApiError(err));
        } finally { setLoading(false); }
    };
    useEffect(() => { load(); }, []);

    const startEdit = (p) => setEditing({ ...editing, [p.id]: { ...p } });
    const cancelEdit = (id) => {
        const next = { ...editing };
        delete next[id];
        setEditing(next);
    };
    const setEditField = (id, field, value) => setEditing({ ...editing, [id]: { ...editing[id], [field]: value } });

    const save = async (id) => {
        const body = editing[id];
        const update = {
            tier: body.tier, tier_name: body.tier_name, max_users: Number(body.max_users),
            name: body.name, duration_days: Number(body.duration_days), warning_days: Number(body.warning_days),
            amount: Number(body.amount), currency: body.currency, active: body.active,
        };
        try {
            await api.patch(`/admin/plans/${id}`, update);
            toast.success("Plano atualizado");
            await load();
        } catch (err) { toast.error(formatApiError(err)); }
    };

    const toggleActive = async (p) => {
        try {
            await api.patch(`/admin/plans/${p.id}`, { active: !p.active });
            toast.success(p.active ? "Plano desativado" : "Plano ativado");
            await load();
        } catch (err) { toast.error(formatApiError(err)); }
    };

    const create = async (e) => {
        e.preventDefault();
        const body = {
            ...draft,
            max_users: Number(draft.max_users),
            duration_days: Number(draft.duration_days),
            warning_days: Number(draft.warning_days),
            amount: Number(draft.amount),
        };
        try {
            await api.post("/admin/plans", body);
            toast.success("Plano criado");
            setCreating(false);
            setDraft(EMPTY_PLAN);
            await load();
        } catch (err) { toast.error(formatApiError(err)); }
    };

    return (
        <div className="space-y-8" data-testid="admin-plans-page">
            <div className="flex flex-wrap justify-between gap-4">
                <div>
                    <Link to="/app/super" className="label-caps inline-flex items-center gap-2" data-testid="back-to-super">
                        <ArrowLeft size={12} /> Super Admin
                    </Link>
                    <h1 className="font-display text-5xl text-[#0d1628] mt-4">Planos & preços</h1>
                    <p className="text-[#52525b] mt-3 max-w-3xl">
                        Gerir planos disponíveis para subscrição. Preço/duração/limite de utilizadores
                        ficam bloqueados se houver subscrições ativas (criar novo plano em alternativa).
                    </p>
                </div>
                <div>
                    <button type="button" onClick={() => setCreating(!creating)} className="btn-primary" data-testid="new-plan-btn">
                        <Plus size={14} /> Novo plano
                    </button>
                </div>
            </div>

            {creating && (
                <form onSubmit={create} className="card-flat" data-testid="new-plan-form">
                    <div className="label-caps mb-4">Novo plano</div>
                    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                        <Field label="ID *"><input className="input-clean" value={draft.id} onChange={(e) => setDraft({ ...draft, id: e.target.value })} placeholder="ex: starter_3m" required data-testid="new-plan-id" /></Field>
                        <Field label="Nome *"><input className="input-clean" value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} required data-testid="new-plan-name" /></Field>
                        <Field label="Tier"><select className="input-clean" value={draft.tier} onChange={(e) => setDraft({ ...draft, tier: e.target.value, tier_name: TIERS.find(t => t.id === e.target.value)?.name || "" })}>{TIERS.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}</select></Field>
                        <Field label="Max utilizadores"><input className="input-clean" type="number" value={draft.max_users} onChange={(e) => setDraft({ ...draft, max_users: e.target.value })} /></Field>
                        <Field label="Duração (dias) *"><input className="input-clean" type="number" value={draft.duration_days} onChange={(e) => setDraft({ ...draft, duration_days: e.target.value })} required /></Field>
                        <Field label="Aviso (dias)"><input className="input-clean" type="number" value={draft.warning_days} onChange={(e) => setDraft({ ...draft, warning_days: e.target.value })} /></Field>
                        <Field label="Preço (€) *"><input className="input-clean" type="number" step="0.01" value={draft.amount} onChange={(e) => setDraft({ ...draft, amount: e.target.value })} required /></Field>
                        <Field label="Moeda"><input className="input-clean" value={draft.currency} onChange={(e) => setDraft({ ...draft, currency: e.target.value })} /></Field>
                    </div>
                    <div className="mt-5 flex gap-2 justify-end">
                        <button type="button" onClick={() => { setCreating(false); setDraft(EMPTY_PLAN); }} className="btn-ghost">Cancelar</button>
                        <button type="submit" className="btn-primary" data-testid="create-plan-btn"><Save size={14} /> Criar</button>
                    </div>
                </form>
            )}

            <div className="border border-[#e4e4e7] overflow-x-auto">
                <table className="w-full text-sm">
                    <thead>
                        <tr className="border-b border-[#e4e4e7] bg-[#f5f3ee]">
                            <th className="text-left p-3 label-caps">ID</th>
                            <th className="text-left p-3 label-caps">Nome</th>
                            <th className="text-left p-3 label-caps">Tier</th>
                            <th className="text-right p-3 label-caps">Max users</th>
                            <th className="text-right p-3 label-caps">Dias</th>
                            <th className="text-right p-3 label-caps">Preço</th>
                            <th className="text-right p-3 label-caps">Subs</th>
                            <th className="text-center p-3 label-caps">Ativo</th>
                            <th className="text-right p-3 label-caps">Ações</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr><td colSpan={9} className="p-6 text-center text-[#52525b]">A carregar...</td></tr>
                        ) : plans.length === 0 ? (
                            <tr><td colSpan={9} className="p-6 text-center text-[#52525b]">Sem planos.</td></tr>
                        ) : plans.map((p) => {
                            const e = editing[p.id];
                            const locked = p.active_subs > 0;
                            return (
                                <tr key={p.id} className="border-b border-[#e4e4e7] last:border-0" data-testid={`plan-row-${p.id}`}>
                                    <td className="p-3 text-[#52525b] font-mono text-xs">{p.id}</td>
                                    <td className="p-3 text-[#0d1628]">
                                        {e ? <input className="input-clean text-sm" value={e.name} onChange={(ev) => setEditField(p.id, "name", ev.target.value)} /> : p.name}
                                    </td>
                                    <td className="p-3 text-[#52525b]">{p.tier_name}</td>
                                    <td className="p-3 text-right">
                                        {e ? <input className="input-clean text-sm w-20 text-right" type="number" value={e.max_users} disabled={locked} onChange={(ev) => setEditField(p.id, "max_users", ev.target.value)} /> : p.max_users}
                                    </td>
                                    <td className="p-3 text-right">
                                        {e ? <input className="input-clean text-sm w-20 text-right" type="number" value={e.duration_days} disabled={locked} onChange={(ev) => setEditField(p.id, "duration_days", ev.target.value)} /> : p.duration_days}
                                    </td>
                                    <td className="p-3 text-right text-[#0d1628] font-medium">
                                        {e ? <input className="input-clean text-sm w-24 text-right" type="number" step="0.01" value={e.amount} disabled={locked} onChange={(ev) => setEditField(p.id, "amount", ev.target.value)} /> : `€${Number(p.amount).toFixed(2)}`}
                                    </td>
                                    <td className="p-3 text-right text-[#52525b]">{p.active_subs}/{p.total_subs}</td>
                                    <td className="p-3 text-center">{p.active ? <span className="text-[#10b981] text-xs">●  Ativo</span> : <span className="text-[#52525b] text-xs">○  Inativo</span>}</td>
                                    <td className="p-3 text-right">
                                        <div className="flex gap-1 justify-end">
                                            {locked && <span title="Tem subscrições ativas — alguns campos estão bloqueados" className="text-[#b89b6f] inline-flex items-center"><Lock size={12} /></span>}
                                            <button type="button" onClick={() => toggleActive(p)} className="btn-ghost text-xs" data-testid={`toggle-${p.id}`}>
                                                {p.active ? <PowerOff size={12} /> : <Power size={12} />}
                                            </button>
                                            {e ? (
                                                <>
                                                    <button type="button" onClick={() => save(p.id)} className="btn-primary text-xs" data-testid={`save-${p.id}`}><Save size={12} /></button>
                                                    <button type="button" onClick={() => cancelEdit(p.id)} className="btn-ghost text-xs">×</button>
                                                </>
                                            ) : (
                                                <button type="button" onClick={() => startEdit(p)} className="btn-outline text-xs" data-testid={`edit-${p.id}`}>Editar</button>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

function Field({ label, children }) {
    return (
        <div>
            <label className="label-caps block mb-2">{label}</label>
            {children}
        </div>
    );
}
